import React, { ReactNode, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { setApiErrorHandler } from "../../api/apiClient";

interface APIErrorContextValue {
  error: string | null;
  addError: (error: unknown) => void;
  removeError: () => void;
}

export const APIErrorContext = React.createContext<APIErrorContextValue>({
  error: null,
  addError: () => {},
  removeError: () => {},
});

export default function APIErrorProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<string | null>(null);

  const removeError = () => setError(null);

  const formatError = (error: unknown): string | null => {
    if (!error) return null;
    if (typeof error === "string") return error;

    // Handle axios error responses
    if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data;
      if (typeof data === "string") return data;

      // A failed FILE DOWNLOAD asked for `responseType: "blob"`, so axios hands back the error body
      // as a Blob even though it is really JSON or text. `JSON.stringify(new Blob(...))` is `"{}"`,
      // so the dialog literally used to say `{}` — the user was told a failure had happened and
      // nothing else. Reading a Blob is async and this function is not, so report the status code,
      // which is at least actionable ("500" vs "404") and honest.
      if (typeof Blob !== "undefined" && data instanceof Blob) {
        return `The request failed (HTTP ${error.response.status}). Please try again.`;
      }

      if (data.message) return data.message;
      if (typeof data === "object") return JSON.stringify(data);
    }

    // Handle error objects with message property
    if (error instanceof Error) return error.message;

    // Handle plain objects
    if (typeof error === "object") {
      // Prefer a `message` field before falling back to dumping the whole object as JSON. Callers
      // like MtcList pass the response BODY straight through (error.response.data), so a structured
      // error `{ code, message, ... }` used to render as raw JSON in the dialog.
      const message = (error as { message?: unknown }).message;
      if (typeof message === "string") return message;

      try {
        return JSON.stringify(error);
      } catch {
        return "An unexpected error occurred";
      }
    }

    return "An unexpected error occurred";
  };

  // Whether the server asked us to stay quiet about this error. `suppressErrorDialog` rides on the
  // response body; some callers hand addError the raw axios error, others (MtcList) hand it the body
  // they already extracted, so check both shapes. It is how the pending-approval 403 — an expected
  // account state, not a failure — is shown as a banner rather than a popup.
  const isSuppressed = (error: unknown): boolean => {
    if (!error || typeof error !== "object") return false;
    if (axios.isAxiosError(error)) {
      return error.response?.data?.suppressErrorDialog === true;
    }
    return (error as { suppressErrorDialog?: boolean }).suppressErrorDialog === true;
  };

  const addError = useCallback((error: unknown) => {
    if (isSuppressed(error)) return;
    setError(formatError(error));
  }, []);

  // Bridge the context-bound error handler to the module-level axios client, whose response
  // interceptor surfaces API errors but can't call a hook. `addError` is stable, so this runs once.
  useEffect(() => {
    setApiErrorHandler(addError);
  }, [addError]);

  const contextValue: APIErrorContextValue = {
    error,
    addError,
    removeError: useCallback(() => removeError(), []),
  };

  return (
    <APIErrorContext.Provider value={contextValue}>
      {children}
    </APIErrorContext.Provider>
  );
}
